import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { timeAxisZoom, timeChartToolbox, ZOOMED_GRID_BOTTOM } from './timeZoom';

export const OrientationRecordChart = ({ orientationRecords, depthRecords, height = 600 }) => {
    const { t } = useTranslation();
    // Memoised so a parent re-render does not re-apply the option and throw
    // away the user's zoom (see timeZoom.js).
    const option = useMemo(() => {
        const records = orientationRecords || [];
        // The legend lists series by name, so both read the same variables.
        const names = {
            gyroX: t('charts.gyroX'), gyroY: t('charts.gyroY'), gyroZ: t('charts.gyroZ'),
            heading: t('charts.heading'), pitch: t('charts.pitch'), roll: t('charts.roll')
        };

        // Some tools only have a 2-axis gyro; the Z channel (GyroZ) is then reported
        // as a constant zero. Hide the Gyro Z series (and its legend entry) when no
        // record carries a non-zero Z reading.
        const hasGyroZ = records.some(r => r.GyroZ);

        const depthMarkLines = (depthRecords || []).map(record => {
            const label = record && record.Depth !== undefined ? `${record.Depth}` : '';
            return {
                xAxis: record.TimeStamp,
                name: label
            };
        });

        return {
            title: {
                text: t('charts.orientationRecords'),
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' }
            },
            toolbox: timeChartToolbox,
            dataZoom: timeAxisZoom(),
            legend: {
                top: 30,
                data: [names.gyroX, names.gyroY, ...(hasGyroZ ? [names.gyroZ] : []), names.heading, names.pitch, names.roll]
            },
            grid: {
                top: 70,
                bottom: ZOOMED_GRID_BOTTOM
            },
            xAxis: {
                type: 'time',
                scale: true,
                splitLine: { show: true }
            },
            yAxis: [
                {
                    name: t('tableTab.gyro'),
                    type: 'value',
                    position: 'left',
                    splitLine: { show: true }
                },
                {
                    name: t('charts.angleDegrees'),
                    type: 'value',
                    position: 'right',
                    splitLine: { show: false }
                }
            ],
            series: [
                {
                    name: names.gyroX,
                    type: 'line',
                    yAxisIndex: 0,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.GyroX])
                },
                {
                    name: names.gyroY,
                    type: 'line',
                    yAxisIndex: 0,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.GyroY])
                },
                ...(hasGyroZ ? [{
                    name: names.gyroZ,
                    type: 'line',
                    yAxisIndex: 0,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.GyroZ])
                }] : []),
                {
                    name: names.heading,
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.Heading])
                },
                {
                    name: names.pitch,
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.Pitch])
                },
                {
                    name: names.roll,
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: false,
                    data: records.map(r => [r.TimeStamp, r.Roll])
                },
                {
                    name: 'DepthMarks',
                    type: 'line',
                    data: [],
                    tooltip: { show: false },
                    showSymbol: false,
                    hoverAnimation: false,
                    silent: true,
                    markLine: {
                        symbol: ['none', 'none'],
                        silent: true,
                        zlevel: 10,
                        label: {
                            formatter: params => params.name || '',
                            position: 'end',
                            color: '#333',
                            fontSize: 11
                        },
                        lineStyle: {
                            color: 'rgba(255, 0, 0, 0.7)',
                            type: 'dashed',
                            width: 1
                        },
                        data: depthMarkLines
                    }
                }
            ]
        };
    }, [orientationRecords, depthRecords, t]);

    return (
        <ReactEcharts
            option={option}
            // Replace rather than merge, so nothing from a previously rendered
            // survey survives into this one. See GyroRecordChart.
            notMerge={true}
            style={{ height: height + 'px', width: '100%' }}
        />
    );
};
